/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import {
	Popper,
	Box,
	Grid,
	Divider,
	Paper,
	Fade,
	Dialog,
	Slide,
	IconButton,
} from '@mui/material';
import useTheme from '../../hooks/useTheme';
import {
	BulbIcon,
	BulbIconColored,
	Close,
	BackIcon,
	OpenWiFiSiteIcon,
} from '../../assets/icons/index';
import Button from '../../components/buttons/Button';
import './FormModal.scss';
import Label from '../../components/label/Label';
import PropTypes from 'prop-types';
import ButtonIcon from '../buttonIcon/ButtonIcon';
import Tooltip from '../tooltip/Tooltip';

/**
 * Renders the Modal Form page component.
 *
 * @function
 * @return {JSX.Element} The add ssid page component.
 */
function FormModal({
	onSave,
	onSaveClicked,
	onReset,
	onCancel,
	isOpen,
	setIsOpen,
	title,
	children,
	saveButtonText = 'Save',
	cancelButtonText = 'Cancel',
	resetButtonText = 'Reset',
	isCancelButton = true,
	isRestButton = true,
	disableSave = false,
	disableReset = false,
	showInfoIcon = false,
	onClickInfo,
	infoButtonTooltipText,
	infoButtonTooltipWidth,
	isInfoActive,
	backButton = false,
	showOpenWifiIcon = false,
}) {
	const { color, FontFace } = useTheme('LIGHT');

	const modalRef = useRef(null); // Reference for the modal content
	const [jiggle, setJiggle] = useState(false);

	// Handle clicks outside the modal
	const handleOutsideClick = (event) => {
		if (
			modalRef.current &&
			!modalRef.current.contains(event.target) &&
			!event.target.closest('[data-inside-modal]') &&
			!event.target.closest('.dropdown-menu') && // Exclude dropdown menus
			!event.target.closest('.MuiPopover-root') && // Exclude Material UI Popovers
			!event.target.closest('.MuiMenu-root') && 
			!event.target.closest('.MuiSelect-root') &&
			!event.target.closest('.MuiInputBase-root')
		) {
			setJiggle(true); // Trigger jiggle animation
			setTimeout(() => setJiggle(false), 500); // Reset after animation
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.addEventListener('mousedown', handleOutsideClick);
		}
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [isOpen]);

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={(event, reason) => {
					if (reason && reason !== 'backdropClick') {
						setIsOpen(false); // Only close if not a backdrop click
					}
				}}
				fullWidth
				maxWidth="md"
				TransitionComponent={Slide}
				TransitionProps={{ direction: 'left' }}
                slotProps={{
                    backdrop: {
                    sx: { backgroundColor: "transparent" }, // Removes backdrop
                    },
                }}
                PaperProps={{
                    sx: { 
                    boxShadow: "none", // Removes shadow
                    elevation: 0, // No elevation
                    backgroundColor: "transparent" // Optional: Makes the dialog itself transparent
                    },
                }}
			>
				<Box>
					<Popper
						ref={modalRef}
						className={`add-modal-form ${
							jiggle ? 'jiggle-animation' : ''
						}`}
						open={isOpen}
						placement={'bottom-end'}
						transition
						style={{
							top: '56px',
							left: 'unset',
							backgroundColor: color.background.primary,
							borderLeft: `solid 1px ${color.stroke.light}`,
							overflow: 'auto',
							boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.16)',
							borderRadius: '8px',
						}}
					>
						{({ TransitionProps }) => (
							<Fade {...TransitionProps} timeout={2}>
								<Paper
									className="add-form-inside-modal"
									style={{
										height: '100%',
										overflowY: 'auto',
										overflowX: 'hidden',
										paddingBottom: '80px',
									}}
								>
									{/* title  */}
									<Grid
										style={{
											position: 'sticky',
											top: 0,
											zIndex: 1300,
											backgroundColor:
												color.background.primary,
											padding: '24px',
											flex: 1,
										}}
									>
										<div
											className="add-form-title-row"
											// style = {{
											// 	borderBottom: `solid 2px ${color.input.line_active}`,
											// 	marginBottom: '16px'
											// }}
										>
											{backButton ? (
												<div
													style={{
														display: 'flex',
														flexDirection: 'row',
														marginTop: '8px',
													}}
												>
													<div onClick={onCancel}>
														<BackIcon />
													</div>
													<div>
														<Label
															label={title}
															type={'primary'}
															fontFamily={
																'Inter-Bold'
															}
															fontSize={'medium'}
														/>
													</div>
												</div>
											) : (
												<Label
													label={title}
													type={'primary'}
													fontFamily={'Inter-Bold'}
													fontSize={'medium'}
												/>
											)}

											<div
												style={{
													alignSelf: 'flex-end',
													flexDirection: 'row',
													display: 'flex',
													alignItems: 'center',
												}}
											>
												{!!showOpenWifiIcon && (
													<OpenWiFiSiteIcon
														style={{
															width: '74px',
															height: '32px',
															marginLeft: '7px',
															marginRight: '16px',
														}}
													/>
												)}
												{showInfoIcon && (
													<Tooltip
														content={
															infoButtonTooltipText
														}
														type="basic"
														direction="bottom"
														delay={300}
														componentsProps={{
															tooltip: {
															sx: {
																maxWidth: `${infoButtonTooltipWidth}px`,
															},
															},
														}}
													>
														<IconButton
															onClick={(e) =>
																onClickInfo(e)
															}
															sx={{
																width: '48px',
																height: '48px',
																padding: '8px',
																backgroundColor:
																	'transparent',
																cursor: 'pointer',

																borderRadius: 0,

																// '&:hover': {
																// },
															}}
														>
															{isInfoActive ? (
																<BulbIconColored />
															) : (
																<BulbIcon />
															)}
														</IconButton>
													</Tooltip>
												)}
												<ButtonIcon
													tooltipText={'Close'}
													isFill={false}
													color={color}
													Icon={Close}
													onPress={() => {
														setIsOpen();
														onReset();
														onCancel();
													}}
												/>
											</div>
										</div>

										<Divider
											sx={{
												borderBottom: `solid 2px ${color.input.line_active}`,
												marginTop: '16px',
											}}
										/>
									</Grid>
									{/* Body with Children */}
									<Box
										style={{
											paddingLeft: '24px',
											paddingRight: '24px',
										}}
									>
										{children}
									</Box>
									{/* footer button */}
									<Grid
										className="add-form-footer"
										sx={{
											flexDirection: 'row-reverse',
											backgroundColor:
												color.background.primary,
										}}
									>
										<Button
											label={saveButtonText}
											type="primary"
											width={'75px'}
											onClick={onSave}
											disabled={disableSave || onSaveClicked}
										/>
										{isCancelButton && (
											<Grid
												paddingLeft={'12px'}
												paddingRight={'12px'}
											>
												<Button
													label={cancelButtonText}
													type="secondary"
													width={'79px'}
													onClick={onCancel}
												/>
											</Grid>
										)}
										{isRestButton && (
											<Grid
												paddingLeft={
													isCancelButton === false
														? '12px'
														: '0px'
												}
												paddingRight={
													isCancelButton === false
														? '12px'
														: '0px'
												}
											>
												<Button
													label={resetButtonText}
													type="tertiary"
													width={'71px'}
													onClick={onReset}
													disabled={disableReset}
												/>
											</Grid>
										)}
									</Grid>
								</Paper>
							</Fade>
						)}
					</Popper>
				</Box>
			</Dialog>
		</>
	);
}

FormModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	children: PropTypes.elementType,
	onSave: PropTypes.func,
	onSaveClicked: PropTypes.bool,
	onReset: PropTypes.func,
	onCancel: PropTypes.func,
	saveButtonText: PropTypes.string,
	cancelButtonText: PropTypes.string,
	resetButtonText: PropTypes.string,
	isCancelButton: PropTypes.bool,
	isRestButton: PropTypes.bool,
	disableReset: PropTypes.bool,
	disableSave: PropTypes.bool,
	showInfoIcon: PropTypes.bool,
	onClickInfo: PropTypes.func,
	infoButtonTooltipText: PropTypes.string,
	infoButtonTooltipWidth: PropTypes.number,
	isInfoActive: PropTypes.bool,
	backButton: PropTypes.bool,
	showOpenWifiIcon: PropTypes.bool,
};

FormModal.defaultProps = {
	isOpen: false,
	onSave: () => {},
	onReset: () => {},
	onCancel: () => {},
	saveButtonText: 'Save',
	cancelButtonText: 'Cancel',
	resetButtonText: 'Reset',
};

export default FormModal;
